/**
 * Thematic vector layers — spec §F5.2 (`layersOn`) + §D5
 * (`GET /layers/{id}/features?country=&bbox=`) + §F5.1 (`vectorPane`, z450: above
 * everything, so a water point is never buried under a choropleth fill).
 *
 * `points_api` → circle markers, `lines_api` → polylines, `polygons_api` → filled paths,
 * all three in the **domain colour** from `/meta/config.domains`. The mockup carried a
 * per-layer `color`; §D5 does not, and the domain palette is the same palette the mockup's
 * colours were drawn from, so the accordion dot and the marker agree by construction.
 *
 * Country scope is part of the query key (§F1), so the global country selector re-fetches
 * and re-draws without any special-casing here — which is also why `refreshThematicLayers`
 * (the mockup's re-clip-on-country-change hack) has no counterpart.
 *
 * A layer with nothing published answers a valid empty FeatureCollection carrying `note`
 * (Task 1). That draws nothing, and correctly so: the row is on, the legend block says
 * what the symbol would be, and the map is honestly empty.
 *
 * ---
 *
 * **Wave 4 added two things, both because the new feeds are enormous.**
 *
 * *Viewport scoping.* `water_points` holds ~123 000 points and `settlement_extents`
 * ~180 000 polygons; an unscoped fetch is 20 MB against §J1's 2 MB budget. A layer whose
 * §D5 row says `viewport: true` is fetched with the map's own bounds as `?bbox=`, so what
 * comes down is what is on screen. The box is padded and snapped (`viewportBbox`) so a
 * small pan reuses the previous query key instead of firing a request per frame.
 *
 * *Per-feature class colours.* A feature may carry `class`, an index into the layer's own
 * `legend` — WPdx' functional status and the six water-point-density bands. The server
 * sends the class and the catalogue holds the colour, so the map mark and the §F5.6 swatch
 * are the same row read twice and cannot drift. A layer without `class` is drawn in the
 * domain colour exactly as before.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import L from 'leaflet';

import {
  fetchConfig,
  fetchLayerFeatures,
  fetchLayers,
  queryKeys,
  type FeatureBbox,
  type Layer,
  type LayerFeatureProperties,
} from '../../lib/api';
import { escapeHtml } from '../../lib/html';
import { DEFAULT_LAYER_OPACITY, useAppStore, type Country } from '../../lib/state';
import { isVectorLayer } from './layerKinds';
import { useMap } from './mapContext';
import { sameBbox, viewportBbox } from './viewportBbox';

type GeoJsonInput = Parameters<typeof L.geoJSON>[0];

/**
 * Mockup `buildPoints` / `buildLines`. The two offsets are what turn the one §F5.2 opacity
 * slider into the mockup's two fixed values at its default: a point lands on the mockup's
 * `fillOpacity .92` and a line on its `.85` when the slider sits at `DEFAULT_LAYER_OPACITY`
 * (0.58), and both still respond to a drag. A shared raw value would have made vectors
 * markedly fainter than the mockup's at the same nominal setting.
 */
const POINT_RADIUS = 4.5;
const POINT_STROKE = 1.4;
const LINE_WEIGHT = 2;
const POINT_OPACITY_BOOST = 0.34;
const LINE_OPACITY_BOOST = 0.27;
/** Areas read as fills, so they sit *under* the slider rather than being boosted past it. */
const AREA_OPACITY_FACTOR = 0.65;

/** The snapped viewport box, or null for a layer §D5 does not mark `viewport`. */
function useViewportBbox(map: L.Map, enabled: boolean): FeatureBbox | null {
  const [bbox, setBbox] = useState<FeatureBbox | null>(() =>
    enabled ? viewportBbox(map.getBounds()) : null,
  );
  useEffect(() => {
    if (!enabled) {
      setBbox(null);
      return;
    }
    const sync = () =>
      setBbox((previous) => {
        const next = viewportBbox(map.getBounds());
        // Identity matters: a new array with the same numbers is a new query key.
        return sameBbox(previous, next) ? (previous as FeatureBbox) : next;
      });
    sync();
    map.on('moveend zoomend', sync);
    return () => {
      map.off('moveend zoomend', sync);
    };
  }, [map, enabled]);
  return bbox;
}

function VectorLayer({ layer, color }: { layer: Layer; color: string }) {
  const map = useMap();
  const country = useAppStore((s) => s.country);
  const opacity = useAppStore((s) => s.layerOpacity[layer.id] ?? DEFAULT_LAYER_OPACITY);

  const bbox = useViewportBbox(map, Boolean(layer.viewport));
  const { data } = useQuery({
    queryKey: queryKeys.layerFeatures(layer.id, country, bbox),
    queryFn: () => fetchLayerFeatures(layer.id, country as Country, bbox),
  });

  /**
   * `class` → the colour the catalogue gave that class. Returns the domain colour when the
   * feature carries no class or the layer no legend, which is every pre-wave-4 vector layer.
   */
  const classColors = useMemo(() => (layer.legend ?? []).map((entry) => entry.color), [layer.legend]);
  const colorFor = (props: LayerFeatureProperties): string => {
    const index = props.class;
    if (typeof index !== 'number') return color;
    return classColors[index] ?? color;
  };

  // The §F5.2 opacity slider is the only thing here that changes per frame, and it changes
  // nothing about the geometry — so it goes through a ref and is applied with `setStyle`,
  // rather than tearing the whole `L.geoJSON` down and rebuilding it on every drag frame.
  const groupRef = useRef<L.GeoJSON | null>(null);
  const pointStyle = (props: LayerFeatureProperties): L.CircleMarkerOptions => ({
    pane: 'vectorPane',
    radius: POINT_RADIUS,
    color: '#fff',
    weight: POINT_STROKE,
    fillColor: colorFor(props),
    fillOpacity: Math.min(1, opacity + POINT_OPACITY_BOOST),
  });
  // One style function for lines and areas: a `LineString` ignores `fillColor`/`fillOpacity`
  // and a polygon uses all four, so the kind is decided by the geometry rather than by a
  // branch here that could disagree with it.
  const pathStyle = (props: LayerFeatureProperties): L.PathOptions => {
    const stroke = colorFor(props);
    return {
      color: stroke,
      weight: LINE_WEIGHT,
      opacity: Math.min(1, opacity + LINE_OPACITY_BOOST),
      fillColor: stroke,
      fillOpacity: opacity * AREA_OPACITY_FACTOR,
    };
  };
  const styleRef = useRef<{
    point: (p: LayerFeatureProperties) => L.CircleMarkerOptions;
    line: (p: LayerFeatureProperties) => L.PathOptions;
  }>({ point: pointStyle, line: pathStyle });
  styleRef.current = { point: pointStyle, line: pathStyle };

  useEffect(() => {
    if (!data) return;
    // One `L.geoJSON` serves both kinds: `pointToLayer` fires only for Point geometry and
    // `style` only for paths, so the kind is decided by the payload rather than by a
    // branch that could disagree with it.
    const group = L.geoJSON(data as unknown as GeoJsonInput, {
      pane: 'vectorPane',
      pointToLayer: (feature, latlng) =>
        L.circleMarker(
          latlng,
          styleRef.current.point((feature.properties ?? {}) as LayerFeatureProperties),
        ),
      style: (feature) =>
        styleRef.current.line((feature?.properties ?? {}) as LayerFeatureProperties),
      onEachFeature: (feature, lyr) => {
        const props = (feature.properties ?? {}) as LayerFeatureProperties;
        // §I1 LIMITED suppresses `name` (Task 1); the layer's own name is then the most
        // specific true thing there is to say, so the tooltip degrades instead of printing
        // "undefined".
        const title = typeof props.name === 'string' && props.name ? props.name : layer.name;
        // `name`, `site_type` and `status` are `/layers/{id}/features` text and `layer.name`
        // is a `/layers` dataset title — all server strings, all reaching `innerHTML`.
        const detail = [props.site_type, props.category, props.status]
          .filter(Boolean)
          .map(escapeHtml)
          .join(' · ');
        lyr.bindTooltip(
          detail
            ? `<b>${escapeHtml(title)}</b><br><span style="color:#8B958A">${detail}</span>`
            : `<b>${escapeHtml(title)}</b>`,
          { sticky: true, className: 'unit-tip' },
        );
      },
    });
    group.addTo(map);
    groupRef.current = group;
    return () => {
      map.removeLayer(group);
      groupRef.current = null;
    };
    // `points` is derived from `layer.kind`, which is in the dependency list already.
  }, [map, data, layer.kind, layer.name]);

  // Colour and opacity only: repaint in place. `setStyle` reaches the polylines through
  // `L.GeoJSON`, and the circle markers are visited by hand — `setStyle` on a `CircleMarker`
  // takes the same options object `pointToLayer` was given, but `L.GeoJSON.setStyle` only
  // forwards to layers whose `style` option produced them.
  useEffect(() => {
    const group = groupRef.current;
    if (!group) return;
    group.setStyle((feature) =>
      styleRef.current.line((feature?.properties ?? {}) as LayerFeatureProperties),
    );
    group.eachLayer((lyr) => {
      if (lyr instanceof L.CircleMarker) {
        const props = ((lyr.feature as { properties?: LayerFeatureProperties } | undefined)
          ?.properties ?? {}) as LayerFeatureProperties;
        lyr.setStyle(styleRef.current.point(props));
      }
    });
  }, [color, opacity]);

  return null;
}

export function VectorLayers() {
  const role = useAppStore((s) => s.role);
  const layersOn = useAppStore((s) => s.layersOn);
  const { data: layers } = useQuery({ queryKey: queryKeys.layers(role), queryFn: fetchLayers });
  const { data: config } = useQuery({ queryKey: queryKeys.config(), queryFn: fetchConfig });

  const colorOf = new Map((config?.domains ?? []).map((d) => [d.id, d.color]));

  return (
    <>
      {(layers ?? [])
        .filter((l) => layersOn.has(l.id) && isVectorLayer(l))
        .map((l) => (
          <VectorLayer key={l.id} layer={l} color={colorOf.get(l.domain) ?? '#4E7A4E'} />
        ))}
    </>
  );
}

export default VectorLayers;
